import {
  ColumnVisibilityDropdown,
  VisibilityState,
  visibilityStateUrlSerializer,
} from "@/components/ColumnVisibilityToggle";
import { DataTable } from "@/components/DataTable";
import { IndeterminateCheckbox } from "@/components/IndeterminateCheckbox";
import { getSosEntityLineItemHashId } from "@/components/SosEntityLineItemHashLink";
import { TableWrapper } from "@/components/TableWrapper";
import { useColumnSetVisibility } from "@/hooks/useColumnSetVisibility";
import { PersistedStateActionButtons, usePersistedState } from "@/hooks/usePersistedState";
import { withJobTrackingData } from "../withJobTrackingData";
import {
  LineItemTableRow,
  normalizeLineItem,
  normalizeReceivingCost,
  ReceivingCostTableRow,
} from "./landed-cost-table-data";
import { landedCostsTableColumns } from "./landed-costs-table-columns";

const pricingDetailColumnIds = new Set([
  "receipt_currency_code",
  "receipt_exchangeRate",
  "receipt_jobLinesSummary_categorizedCosts_material_subtotal",
  "receipt_jobLinesSummary_categorizedCosts_nonMaterial_subtotal",
  "receipt_jobLinesSummary_categorizedCosts_shipping_subtotal",
  "normalizedLine_totalCost_tax",
  "normalizedLine_totalCost_total",
]);

export const LandedCosts = withJobTrackingData(({ itemReceipts }) => {
  // Persisted column visibility state
  const [columnVisibility, setColumnVisibility, persistedStateActions] = usePersistedState<VisibilityState>({
    componentId: "landed-costs-hidden-cols",
    urlKey: "hidden-cols",
    stateSchema: VisibilityState,
    version: 1,
    defaultState: Object.fromEntries([...pricingDetailColumnIds].map((id) => [id, false])),
    urlSerializer: visibilityStateUrlSerializer,
  });

  // Use hook for toggling visibility on pricing detail columns
  const [showPricingDetails, toggleShowPricingDetails] = useColumnSetVisibility(
    pricingDetailColumnIds,
    columnVisibility,
    setColumnVisibility,
  );

  // Flatten the list of item receipts into a list of table rows (one row for each line item and receiving cost)
  const data = itemReceipts.flatMap((receipt) => [
    ...receipt.jobLines.map<LineItemTableRow>((lineItem, index) => ({
      rowId: getSosEntityLineItemHashId("itemreceipt", receipt.id, index),
      receipt,
      lineItem,
      normalizedLine: normalizeLineItem(lineItem),
    })),
    ...receipt.jobReceivingCosts.map<ReceivingCostTableRow>((receivingCost, index) => ({
      rowId: getSosEntityLineItemHashId("itemreceipt", receipt.id, receipt.jobLines.length + index),
      receipt,
      receivingCost,
      normalizedLine: normalizeReceivingCost(receivingCost, receipt),
    })),
  ]);

  return (
    <>
      <div className="col-auto mb-3">
        <div className="row row-cols-auto gx-3 align-items-center">
          <div>
            <IndeterminateCheckbox
              id="filter-pricing-details"
              label="Show Detailed Pricing"
              checked={showPricingDetails}
              onChange={toggleShowPricingDetails}
            />
          </div>
          <ColumnVisibilityDropdown
            columns={landedCostsTableColumns}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
          />
          <PersistedStateActionButtons {...persistedStateActions} />
        </div>
      </div>
      <TableWrapper className="col">
        <DataTable
          bordered
          hover
          data={data}
          columns={landedCostsTableColumns}
          getRowId={({ rowId }) => rowId}
          state={{ columnVisibility }}
          initialState={{
            sorting: [{ id: "receipt_date", desc: true }],
          }}
        />
      </TableWrapper>
    </>
  );
});
